import React from 'react';
import { capitalize } from 'inflection';
import moment from 'moment';

import Heading from 'buyside/components/presentation/ContributorDashboard/contributorDashboardHeading';
import Link from 'buyside/components/presentation/Link/link';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { ideaUrl, userUrl } from 'buyside/util/urlHelper';
const { viewerIntroRequestStatuses } = require('buyside/util/constantsFromRails.json');

import styles from 'buyside/components/presentation/ContributorDashboard/contributorDashboardStyles.scss';

import { useCurrentUserSentIntroRequestsQuery } from 'interfaces/graphql-typings';

const ViewerIntroRequestsSent: React.FC = () => {
  const { data, loading, error } = useCurrentUserSentIntroRequestsQuery();

  if (loading)
    return (
      <>
        <Heading title="Sent Intro Requests" />
        <LoadingSpinner />
      </>
    );

  const currentUser = data?.currentUser;

  if (!currentUser || !currentUser?.sentViewerIntroRequests) return null;

  const { sentViewerIntroRequests } = currentUser;

  if (!sentViewerIntroRequests?.length)
    return (
      <>
        <Heading title="Sent Intro Requests" />
        <span>There are no intro requests sent</span>
      </>
    );

  if (error) throw error;

  return (
    <>
      <Heading title="Sent Intro Requests" />
      <p>
        Below you will find the introduction requests that you have sent to members who have viewed
        your ideas. Once a request has been accepted by the viewer, you will be able to message them
        on SumZero.
      </p>
      <table className={styles.tableContainer}>
        <thead>
          <tr>
            <th>Date Sent</th>
            <th>Status</th>
            <th>Viewer</th>
            <th>Report</th>
          </tr>
        </thead>
        <tbody>
          {sentViewerIntroRequests?.map(sentRequest => {
            if (!sentRequest) return null;

            const {
              createdAt,
              id,
              status,
              viewableId,
              viewableDisplayTitle,
              viewer,
              ideaView
            } = sentRequest;

            const dateSent = moment(new Date(createdAt), 'YYYY-MM-DD').format('l');

            const pending = status === viewerIntroRequestStatuses.pending;

            const inflectionSubject = ideaView?.viewerCityName || ideaView?.viewerTitle || 'Member';
            const indefiniteArticle = inflectionSubject.charAt(0).match(/a|e|i|o|u/i) ? 'An' : 'A';
            const showAumAndFund =
              ideaView?.viewerAumRange &&
              ideaView.viewerAumRange !== 'N/A' &&
              ideaView.viewerFundType;

            return (
              <tr key={id}>
                <td>{dateSent}</td>

                <td className={pending ? styles.pending : styles.accepted}>{capitalize(status)}</td>

                <td>
                  {status === 'accepted' ? (
                    <Link to={userUrl(viewer.id)}>{viewer.displayName}</Link>
                  ) : (
                    <>
                      {indefiniteArticle}{' '}
                      {ideaView?.viewerCityName && (
                        <strong>{ideaView?.viewerCityName}-based </strong>
                      )}
                      <strong>
                        {ideaView?.viewerTitle && ideaView.viewerTitle !== 'Other'
                          ? ideaView.viewerTitle
                          : 'Member'}
                      </strong>
                      {showAumAndFund
                        ? ` at a ${ideaView?.viewerAumRange} ${ideaView?.viewerFundType} `
                        : ' '}
                    </>
                  )}
                </td>

                <td>
                  <Link to={ideaUrl(viewableId)}> {viewableDisplayTitle}</Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default ViewerIntroRequestsSent;
